import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { Restriction, RestrictionConditions, RestrictionExceptions } from '@mews-distributor/data-access';
import { isNumberGreaterAsDecimals } from '@mews/framework';
import {
    DateService,
    DateTimeService,
    DateTimeUtc,
    DurationService,
    WallClockDateService,
} from '@mews/framework/datetime';
import { MergeRight } from '@mews/std';

interface ViolatedDateMetrics {
    selectedDateLength: number;
    selectedAdvance: number;
}

type RestrictionHeapData = Omit<Restriction, 'conditions' | 'exceptions'> &
    MergeRight<RestrictionConditions, { days: string }> &
    MergeRight<
        RestrictionExceptions,
        {
            maxAdvance: number | null;
            minAdvance: number | null;
            minLength: number | null;
            maxLength: number | null;
        }
    > &
    ViolatedDateMetrics;

export const formatViolatedRestrictions = (
    {
        conditions: { days, ...conditions },
        exceptions: { maxAdvance, minAdvance, minLength, maxLength, ...exceptions },
        id,
        serviceId,
        externalIdentifier,
    }: Restriction,
    { selectedDateLength, selectedAdvance }: ViolatedDateMetrics
): RestrictionHeapData => ({
    id,
    serviceId,
    externalIdentifier,
    days: days.toString(),
    ...conditions,
    maxAdvance: maxAdvance != null ? DurationService.asDays(DurationService.parse(maxAdvance)) : null,
    minAdvance: minAdvance != null ? DurationService.asDays(DurationService.parse(minAdvance)) : null,
    minLength: minLength != null ? DurationService.asDays(DurationService.parse(minLength)) : null,
    maxLength: maxLength != null ? DurationService.asDays(DurationService.parse(maxLength)) : null,
    ...exceptions,
    selectedDateLength,
    selectedAdvance,
});

interface GetViolatedDateMetricsOptions {
    startDateString: string;
    endDateString: string;
    nowUtc: DateTimeUtc;
    timezone: string;
}

export const getViolatedDateMetrics = ({
    startDateString,
    endDateString,
    nowUtc,
    timezone,
}: GetViolatedDateMetricsOptions) => {
    if (!startDateString || !endDateString) {
        return { selectedDateLength: 0, selectedAdvance: 0 };
    }

    const wallClockStartDate = WallClockDateService.parse(startDateString);
    const wallClockEndDate = WallClockDateService.parse(endDateString);

    const startDate = WallClockDateService.toTimezonedDate(wallClockStartDate, timezone);
    const dayDiff = WallClockDateService.diffDays(wallClockEndDate, wallClockStartDate);

    const nowDate = DateTimeService.toDate(DateTimeService.fromUtc(nowUtc, timezone));

    const selectedAdvance = DateService.diffDays(startDate, nowDate);

    return { selectedDateLength: dayDiff, selectedAdvance };
};

export interface AggregatedExceptions {
    maxAdvanceDays: number | null;
    maxLengthDays: number | null;
    minAdvanceDays: number | null;
    minLengthDays: number | null;
    maxPrice: number | null;
    maxReservationCount: number | null;
    minPrice: number | null;
    priceCurrencyCode: string | null;
}

const getIntervalDays = (interval: string) => DurationService.asDays(DurationService.parse(interval));

const convertToComparableExceptions = ({
    minLength,
    maxLength,
    minAdvance,
    maxAdvance,
    maxPrice,
    minPrice,
    priceCurrencyCode,
    maxReservationCount,
}: RestrictionExceptions): AggregatedExceptions => ({
    maxAdvanceDays: maxAdvance != null ? getIntervalDays(maxAdvance) : null,
    maxLengthDays: maxLength != null ? getIntervalDays(maxLength) : null,
    minAdvanceDays: minAdvance != null ? getIntervalDays(minAdvance) : null,
    minLengthDays: minLength != null ? getIntervalDays(minLength) : null,
    maxPrice: maxPrice ?? null,
    minPrice: minPrice ?? null,
    priceCurrencyCode: priceCurrencyCode ?? null,
    maxReservationCount: maxReservationCount ?? null,
});

const getGreaterValue = (prevValue: number | null, nextValue: number | null): number | null => {
    if (prevValue == null) {
        return nextValue;
    }
    if (nextValue == null) {
        return prevValue;
    }
    return isNumberGreaterAsDecimals(prevValue, nextValue) ? prevValue : nextValue;
};

const getSmallerValue = (prevValue: number | null, nextValue: number | null): number | null => {
    if (prevValue == null) {
        return nextValue;
    }
    if (nextValue == null) {
        return prevValue;
    }
    return isNumberGreaterAsDecimals(prevValue, nextValue) ? nextValue : prevValue;
};

export const aggregateExceptions = (exceptions: AggregatedExceptions[]): option.Option<AggregatedExceptions> =>
    pipe(
        exceptions,
        array.reduce<AggregatedExceptions, AggregatedExceptions | null>(null, (prev, next) => {
            if (prev === null) {
                return next;
            }
            const { minLengthDays, maxLengthDays, maxAdvanceDays, minAdvanceDays, ...nextOther } = next;

            return {
                minLengthDays: getSmallerValue(prev.minLengthDays, minLengthDays),
                maxLengthDays: getSmallerValue(prev.maxLengthDays, maxLengthDays),
                minAdvanceDays: getGreaterValue(prev.minAdvanceDays, minAdvanceDays),
                maxAdvanceDays: getSmallerValue(prev.maxAdvanceDays, maxAdvanceDays),
                ...nextOther,
            };
        }),
        option.fromNullable
    );

export const aggregateRestrictions = (restrictions: Restriction[]) =>
    pipe(
        restrictions,
        array.map(({ exceptions }) => convertToComparableExceptions(exceptions)),
        aggregateExceptions
    );

export const isGlobalRestriction = ({ conditions }: Restriction) => {
    const { exactRateId, rateGroupId, baseRateId, resourceCategoryId, resourceCategoryType } = conditions;

    return pipe(
        [exactRateId, rateGroupId, baseRateId, resourceCategoryId, resourceCategoryType],
        array.every((predicate) => predicate === null)
    );
};

export const keepGlobalRestrictions = (restrictions: Restriction[]) =>
    pipe(restrictions, array.filter(isGlobalRestriction));
